// frontend/src/pages/reports/StudentMarkSheetPage.js
import React, { useState, useEffect } from 'react';
import {
  Table, Card, Space, Button, DatePicker, Select, Spin, Typography, Row, Col,
  Statistic, Divider, Empty, Tag, message, Input
} from 'antd';
import { DownloadOutlined, PrinterOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getAllStudents } from '../../services/studentService';
import { getAllExams } from '../../services/examService';
import { getMarksByFilters } from '../../services/markService';
import { useAcademicYear } from '../../context/AcademicYearContext';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const StudentMarkSheetPage = () => {
  const { currentYear } = useAcademicYear();
  const [students, setStudents] = useState([]);
  const [exams, setExams] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [marks, setMarks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [studentDetails, setStudentDetails] = useState(null);
  const [stats, setStats] = useState({ total: 0, average: 0, percentage: 0 });

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [studentsData, examsData] = await Promise.all([
          getAllStudents(),
          getAllExams()
        ]);
        setStudents(studentsData);
        setExams(examsData);
      } catch (error) {
        message.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentYear]);

  // Fetch marks when student is selected
  useEffect(() => {
    if (!selectedStudent) {
      setMarks([]);
      setStudentDetails(null);
      return;
    }

    const fetchMarks = async () => {
      setLoading(true);
      try {
        const student = students.find(s => s.id === selectedStudent);
        setStudentDetails(student);

        // Build params object
        const params = {
          student_id: selectedStudent,
        };

        if (selectedExam) {
          params.exam_id = selectedExam;
        }

        if (dateRange && dateRange.length === 2) {
          params.start_date = dateRange[0].format('YYYY-MM-DD');
          params.end_date = dateRange[1].format('YYYY-MM-DD');
        }

        // console.log('StudentMarkSheetPage - Starting fetch...');
        // console.log('StudentMarkSheetPage - Filters:', params);

        const data = await getMarksByFilters(params);
        // console.log('StudentMarkSheetPage - Fetched data:', data);
        // console.log('StudentMarkSheetPage - Data length:', data.length);

        setMarks(data);

        // Calculate stats
        if (data.length > 0) {
          const total = data.reduce((sum, m) => sum + (parseFloat(m.score) || 0), 0);
          const maxMarks = data.reduce((sum, m) => sum + (parseFloat(m.total_marks) || 0), 0);
          const average = (total / data.length).toFixed(2);
          const percentage = maxMarks > 0 ? ((total / maxMarks) * 100).toFixed(2) : 0;
          setStats({ total, average, percentage });
          // console.log('StudentMarkSheetPage - Calculated Stats:', { total, average, percentage, count: data.length });
        }
      } catch (error) {
        message.error('Failed to fetch marks');
        console.error('StudentMarkSheetPage - Error:', error);
        console.error('StudentMarkSheetPage - Error message:', error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMarks();
  }, [selectedStudent, selectedExam, dateRange, students]);

  const columns = [
    {
      title: 'Exam',
      dataIndex: 'exam_name',
      key: 'exam_name',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Subject',
      dataIndex: 'subject_name',
      key: 'subject_name',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      render: (score) => score ? parseFloat(score).toFixed(2) : '-',
    },
    {
      title: 'Total Marks',
      dataIndex: 'total_marks',
      key: 'total_marks',
      render: (total) => total || '-',
    },
    {
      title: 'Percentage',
      key: 'percentage',
      render: (_, record) => {
        if (!record.score || !record.total_marks) return '-';
        const percent = ((record.score / record.total_marks) * 100).toFixed(2);
        return (
          <Tag color={percent >= 75 ? 'success' : percent >= 50 ? 'warning' : 'error'}>
            {percent}%
          </Tag>
        );
      },
    },
  ];

  const handleExport = () => {
    if (marks.length === 0) {
      message.warning('No data to export');
      return;
    }

    const csv = [
      ['Student Name', 'Exam', 'Subject', 'Score', 'Total Marks', 'Percentage'],
      ...marks.map(m => [
        studentDetails?.student_name || 'N/A',
        m.exam_name || 'N/A',
        m.subject_name || 'N/A',
        m.score || '-',
        m.total_marks || '-',
        m.score && m.total_marks ? ((m.score / m.total_marks) * 100).toFixed(2) : '-',
      ])
    ]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${studentDetails?.student_name}_marks.csv`;
    a.click();
  };

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Title level={2}>📄 Student Mark Sheet</Title>
          <Text type="secondary">
            View detailed mark sheet for individual students with filtering options
          </Text>
        </div>

        {/* Filters */}
        <Card>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={8}>
              <label>Select Student *</label>
              <Select
                placeholder="Choose a student"
                value={selectedStudent}
                onChange={setSelectedStudent}
                loading={loading}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={students.map(s => ({
                  label: `${s.student_name} (${s.admission_no})`,
                  value: s.id,
                }))}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <label>Filter by Exam</label>
              <Select
                placeholder="All Exams"
                value={selectedExam}
                onChange={setSelectedExam}
                allowClear
                options={exams.map(e => ({
                  label: e.name,
                  value: e.id,
                }))}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <label>Date Range</label>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>
        </Card>

        {/* Statistics */}
        {studentDetails && marks.length > 0 && (
          <>
            <Row gutter={16}>
              <Col xs={12} sm={8}>
                <Statistic title="Total Score" value={stats.total.toFixed(2)} />
              </Col>
              <Col xs={12} sm={8}>
                <Statistic title="Average" value={stats.average} />
              </Col>
              <Col xs={12} sm={8}>
                <Statistic
                  title="Percentage"
                  value={stats.percentage}
                  suffix="%"
                  valueStyle={{ color: stats.percentage >= 50 ? '#52c41a' : '#ff4d4f' }}
                />
              </Col>
            </Row>
            <Divider />
          </>
        )}

        {/* Results */}
        {selectedStudent ? (
          <>
            <Card
              title={`Marks for ${studentDetails?.student_name || 'Selected Student'}`}
              extra={
                <Space>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleExport}
                    disabled={marks.length === 0}
                  >
                    Export CSV
                  </Button>
                  <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
                    Print
                  </Button>
                </Space>
              }
            >
              {loading ? (
                <Spin />
              ) : marks.length > 0 ? (
                <Table
                  columns={columns}
                  dataSource={marks.map((m, i) => ({ ...m, key: i }))}
                  pagination={{
                    pageSize: pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50, 100],
                    onShowSizeChange: (current, size) => setPageSize(size),
                    onChange: (page, size) => setPageSize(size)
                  }}
                  scroll={{ x: 600 }}
                />
              ) : (
                <Empty description="No marks found" />
              )}
            </Card>
          </>
        ) : (
          <Empty description="Please select a student to view marks" />
        )}
      </Space>
    </div>
  );
};

export default StudentMarkSheetPage;
